// Fase 3 — Log/historiek "Klant inlichten bij nalevering".
// Analoog aan useSikkensExportLog: realtime, per orderid.
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

export interface CustomerNotificationLogEntry {
  id: string;
  orderid: number;
  template_key: string;
  subject: string;
  recipient: string;
  body_html: string | null;
  sent_at: string;
  sent_by: string | null;
}

const queryKey = (orderid: number | null | undefined) =>
  ["customer-notification-log", orderid ?? "none"] as const;

export const useCustomerNotificationLog = (orderid: number | null | undefined) => {
  const { isAuthenticated, isSessionValid, isValidating } = useAuth();
  const qc = useQueryClient();
  const enabled =
    isAuthenticated && isSessionValid && !isValidating && typeof orderid === "number";

  const query = useQuery({
    queryKey: queryKey(orderid),
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<CustomerNotificationLogEntry[]> => {
      if (typeof orderid !== "number") return [];
      const { data, error } = await supabase
        .from("customer_notification_log")
        .select("id, orderid, template_key, subject, recipient, body_html, sent_at, sent_by")
        .eq("orderid", orderid)
        .order("sent_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as CustomerNotificationLogEntry[];
    },
  });

  useEffect(() => {
    if (!enabled || typeof orderid !== "number") return;
    const channel = supabase
      .channel(`customer-notification-log-${orderid}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "customer_notification_log",
          filter: `orderid=eq.${orderid}`,
        },
        (payload) => {
          const row = payload.new as CustomerNotificationLogEntry | null;
          if (!row) return;
          qc.setQueryData<CustomerNotificationLogEntry[]>(queryKey(orderid), (prev) => {
            const list = prev ?? [];
            if (list.some((r) => r.id === row.id)) return list;
            return [row, ...list];
          });
        },
      )
      .subscribe();
    return () => {
      try {
        supabase.removeChannel(channel);
      } catch {
        /* noop */
      }
    };
  }, [enabled, orderid, qc]);

  const history = query.data ?? [];
  return {
    ...query,
    history,
    isInformed: history.length > 0,
  };
};

export const formatNotifiedAt = (iso: string): string => {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(new Date(t))
    .replace(" om ", ", ");
};
